// Base-class colour system — the SPA mirror of the desktop graph palette
// (crates/tres-desktop/src/ui/styles.rs GRAPH_BASE_COLORS + lighten_color) and
// the permanent base-class → slot assignment (crates/tres-shared-vdb/src/
// qros_foundation.rs BASE_CLASS_COLOR_INDEX). Keep these in sync with those two.
//
// Each of the 11 QROS base classes owns a permanent palette slot (Amber, slot 4,
// is skipped). A class's colour is its base ancestor's slot, LIGHTENED by depth
// from that base — so a subtree shares one colour family, fading with depth.

// 12-colour graph palette (RGB from styles.rs). Slot 4 (Amber) is reserved-unused.
// All channels web-safe (00/33/66/99/CC/FF), one step around the hue wheel per
// slot: the old set paired Dark Purple with Indigo and Dark Red with Crimson, so
// two large families read as the same colour. One violet, one magenta, no repeats.
export const GRAPH_BASE_COLORS: string[] = [
  '#000099', // 0  Navy
  '#009999', // 1  Teal
  '#006600', // 2  Green
  '#cc0000', // 3  Red
  '#cc9900', // 4  Amber — skipped
  '#6600cc', // 5  Violet
  '#663300', // 6  Brown
  '#cc0099', // 7  Magenta
  '#cc6600', // 8  Orange
  '#990033', // 9  Maroon
  '#669900', // 10 Lime
  '#0066cc', // 11 Azure
];

// Permanent slot per base-class local name (skips Amber/4). Mirrors qros_foundation.
export const BASE_CLASS_COLOR_INDEX: Record<string, number> = {
  Person: 0,
  Species: 1,
  Organization: 2,
  Event: 3,
  Place: 5,
  TangibleObject: 6,
  CreativeWork: 7,
  Concept: 8,
  Action: 9,
  DataSet: 10,
  Vocabulary: 11,
};

/** The 11 base-class local names, in canonical order (for legends). */
export const BASE_CLASS_NAMES = Object.keys(BASE_CLASS_COLOR_INDEX);

/** Strip a `qros:`/`:`/IRI prefix to the base-class local name, or null. The
 *  native namespace is written `:Name` (the blank default prefix) and `qros:Name`
 *  interchangeably — both resolve to the same base class. */
export function baseClassLocalName(uri: string): string | null {
  const local = uri.replace(/^(qros)?:/, '').replace(/^.*[#/]/, '');
  return local in BASE_CLASS_COLOR_INDEX ? local : null;
}

/** Lighten a hex colour toward white: 20% per depth level, capped at 70%
 *  (port of styles.rs::lighten_color). depth 0 = the base colour itself. */
export function lighten(hex: string, depth: number): string {
  const blend = Math.min(depth * 0.2, 0.7);
  const n = parseInt(hex.slice(1), 16);
  const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) =>
    Math.round(c + (255 - c) * blend),
  );
  return `#${ch.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/** The full-saturation family colour for a base-class local name. */
export function colorForBaseClass(localName: string): string | null {
  const idx = BASE_CLASS_COLOR_INDEX[localName];
  return idx === undefined ? null : (GRAPH_BASE_COLORS[idx] ?? null);
}

/** The base class that owns a palette slot, or null for a slot no base class
 *  owns (a non-base top class on an RDF store takes a spare slot). */
export function baseClassForSlot(index: number | null | undefined): string | null {
  if (index === null || index === undefined) return null;
  return BASE_CLASS_NAMES.find((name) => BASE_CLASS_COLOR_INDEX[name] === index) ?? null;
}

/** The family colour for a raw palette slot (0–11), as the VDB hands it to the
 *  viz via `color_index`. Optionally lightened by depth. Returns null for an
 *  out-of-range or missing slot. The viz uses this; the ontology editor uses the
 *  by-name helpers above. Both draw from the one palette. */
export function colorByIndex(index: number | null | undefined, depth = 0): string | null {
  if (index === null || index === undefined) return null;
  const family = GRAPH_BASE_COLORS[index];
  return family ? lighten(family, depth) : null;
}

/** Readable text colour (dark or white) for a background hex — perceived
 *  brightness, matching styles.rs::text_color_for_background. */
export function textColorFor(hex: string): string {
  const n = parseInt(hex.slice(1), 16);
  const lum = 0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255);
  return lum > 160 ? '#1d1d1f' : '#ffffff';
}
